/**
 * A class that was deleted, and what every screen is allowed to say about it.
 *
 * Bobbie, 2026-09-15 6:53am Central, verbatim: "When I click on this class
 * that has been deleted but it is still showing up in the calendar". The
 * cause was one line: deleteOffering was a bare deleteDoc with no cascade, so
 * a deleted class left behind its weekly times, every booking they had made,
 * and each future booking's room lock. The Schedule Grid went on drawing
 * those bookings, printed the word "Class" where the teacher's name goes, and
 * sent a tap to a class page that answers "Class not found".
 *
 * PURE, AND FIREBASE-FREE ON PURPOSE. Three screens have to agree about the
 * same question - is this booking's class still there - and each of them
 * answered it inline, differently. The decisions live here so a plain node
 * test can pin them, the same discipline lib/staleClassBookings.ts follows.
 *
 * THE THREE-WAY ANSWER IS THE WHOLE POINT. "Not in the offerings map" is NOT
 * proof a class is gone: useOfferings orders by name and Firestore's
 * orderBy(field) silently drops any document missing that field, so a real
 * class written without a name never appears in that list however long the
 * listener runs (lib/offeringResolution.ts). A screen that treated absence as
 * death would label a live class "Class removed" and offer to cancel it off
 * the calendar. So "checking" is a state of its own, and only a direct getDoc
 * that came back empty is allowed to say "removed".
 */

/**
 * Where a booking's class stands.
 *
 *   noClass  - the booking carries no offeringId at all. A rehearsal, an
 *              event, a block, or a hand-made booking that was never tied to
 *              a class. Nothing here is about it.
 *   present  - the class document is in hand.
 *   checking - the id is not in the live list and no direct lookup has
 *              answered yet, OR a lookup was tried and FAILED. A refused read
 *              says nothing about whether the class exists, so it stays here
 *              rather than graduating to `removed`.
 *   removed  - a direct read came back empty. Confirmed gone.
 */
export type ClassResolution = 'noClass' | 'present' | 'checking' | 'removed';

/**
 * `offeringFound` is "the class document is in hand" and `stillChecking` is
 * "no lookup has answered for this id yet". Callers on the client get both
 * from useResolvedOfferingsById (lib/offerings.ts): found is
 * offeringsById.has(id), still-checking is its isResolving(id), which stays
 * true for a read that failed exactly as it does for one still in flight.
 */
export function classBookingResolution(
  offeringId: string | null | undefined,
  offeringFound: boolean,
  stillChecking: boolean
): ClassResolution {
  if (typeof offeringId !== 'string' || offeringId.length === 0) return 'noClass';
  if (offeringFound) return 'present';
  if (stillChecking) return 'checking';
  return 'removed';
}

/** What a grid tile prints where the class kind goes. */
export const CLASS_REMOVED_LABEL = 'Class removed';

/**
 * The kind label for a booking whose class could not be drawn. `fallback` is
 * whatever the screen would have said anyway (RESERVATION_KIND_LABELS.class
 * on the Schedule Grid). Only a CONFIRMED removal changes the word: while a
 * lookup is still out, the tile keeps saying "Class", because it does not yet
 * know anything a person does not.
 */
export function classBookingKindLabel(resolution: ClassResolution, fallback: string): string {
  return resolution === 'removed' ? CLASS_REMOVED_LABEL : fallback;
}

/**
 * Where a tap on that booking goes.
 *
 * A CONFIRMED-GONE ID NEVER GOES TO /offerings/<id>, which is the exact tap
 * Bobbie reported: it lands on the class page's "Class not found" panel, a
 * dead end with nothing on it. It goes to the booking on the calendar
 * instead, which is the route an offering-less booking already takes and the
 * one screen that can actually do something about it.
 *
 * `checking` deliberately keeps the class route. The id is probably real and
 * simply not resolved yet, and both listeners on the grid start together, so
 * sending every tile to the calendar for the first moments of every page load
 * would trade a rare dead end for a common wrong turn. A real class opens; a
 * dead one shows the honest not-found panel it shows today, for the one beat
 * before the lookup lands.
 */
export function classBookingRoute(
  resolution: ClassResolution,
  offeringId: string | null | undefined,
  reservationId: string,
  /** The booking's studio day, 'YYYY-MM-DD', when the caller knows it, so the
   * calendar opens on that day rather than today (N27). */
  dateKey?: string | null
): string {
  if (resolution === 'present' || resolution === 'checking') return `/offerings/${offeringId}`;
  return dateKey ? `/calendar?d=${dateKey}&focusId=${reservationId}` : `/calendar?focusId=${reservationId}`;
}

/**
 * The server's own word for "there is no such class", as
 * netlify/functions/cancelClass.mjs sends it. Spelled once here so the
 * client's fall-back test and lib/cancelClass.ts's friendly-message table can
 * never drift apart.
 */
export const OFFERING_NOT_FOUND_CODE = 'offering_not_found';

/**
 * Should the calendar's Cancel drop out of the durable class-cancel lane and
 * just remove this one booking?
 *
 * Only for THIS error. The durable lane exists so the recurring-series safety
 * net cannot put a cancelled class back, and it stays the lane for every
 * booking whose class exists. But a booking whose class is deleted can never
 * succeed there: the server has no offering to resolve a roster from, answers
 * offering_not_found, and removes nothing - so the booking Bobbie tapped
 * Cancel on stayed on the calendar, which is the second half of the same bug.
 * Falling back is allowed because the rules already let a Director or the
 * booking's creator delete a reservation outright (shared/firestore.rules,
 * the reservations delete rule), so this asks for nothing new.
 *
 * Matched on the server's CODE, never on the sentence a person reads: the
 * friendly copy is free to change, the code is a contract.
 */
export function shouldFallBackToPlainCancel(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  return (error as { code?: unknown }).code === OFFERING_NOT_FOUND_CODE;
}

/** What the calendar says after that fall-back removed the booking. */
export const DELETED_CLASS_CANCEL_NOTICE =
  'That class has been deleted, so this session was taken off the calendar on its own. '
  + 'Nobody was told, and sessions already in the past are untouched.';

/** a, b and c. Empty and one-item lists read naturally. */
function joinList(parts: readonly string[]): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * What the Delete button promises BEFORE it is pressed.
 *
 * The roster count is the only one the card can honestly show at that moment:
 * offering.studentIds is already in hand, while the weekly times and the
 * booked sessions live in two other collections this card never reads. So the
 * times and the sessions are described rather than counted, and no number
 * appears that was not measured. Bobbie's standing rule against inventing a
 * fact applies to a count as much as to a sentence. A class with nobody on it
 * gets NO dancer clause at all, not "0 dancers": zero is a count too, and the
 * sentence reads better without it.
 *
 * "THE N DANCERS ON THE ROSTER", NEVER "ALL N DANCERS". The count in front of
 * a Director is the offering's own studentIds, and the cascade releases the
 * UNION of that roster and every dancer whose own `memberships` array names
 * this class - the half-link deleteOffering exists to heal. Those two can
 * disagree, so the real number coming off can be HIGHER than the one shown
 * here, and a sentence saying "all" would be a promise this card cannot keep.
 * Scoping the count to the roster is the true version of the same sentence.
 * The summary afterwards reports what actually came off, counted by the
 * function that did it.
 *
 * REHEARSALS ARE NAMED HERE because they are swept. A rehearsal always
 * carries an offeringId (shared/firestore.rules gates the kind on it), so
 * deleteOffering's loose-booking pass takes this class's future rehearsals
 * off with its sessions. Somebody planned around those, so the sentence says
 * the word rather than hiding them inside "sessions".
 */
export function deleteOfferingConfirmCopy(dancerCount: number): string {
  const dancers = dancerCount > 0
    ? `takes ${dancerCount === 1 ? 'the 1 dancer' : `the ${dancerCount} dancers`} on the roster off this class, `
    : '';
  return `Delete ${dancers}removes its weekly times, and cancels every session and rehearsal still `
    + 'ahead on the calendar so the rooms go free. Sessions already in the past stay as history. '
    + `Nobody is told. ${DELETED_CLASS_PUT_BACK_NOTE}`;
}

/**
 * WHERE A DELETED CLASS COMES BACK FROM (bug B22). Bobbie, 2026-08-06: "I
 * want an undo button to be present for 1 hour.... and then a place to
 * completly undo maybe under studio settings.... but only up to a month".
 * Said before the tap and again after it, so the way back is never a secret.
 */
export const DELETED_CLASS_PUT_BACK_NOTE =
  'You can put it back: Undo on the Classes page for an hour, then Recently deleted classes in '
  + 'Studio Settings for thirty days.';

/**
 * A class made by a workshop (offerings.signupEventId set) is never deleted
 * from Classes. The class delete would leave the workshop open for signups
 * with no class behind it, and every later approve or decline would fail.
 * The workshop owns that class, so the Classes page points there instead,
 * and deleteOffering refuses with this same sentence before it changes
 * anything.
 */
export function workshopClassDeleteCopy(name: string): string {
  return `${name} belongs to a workshop, so it is not deleted from Classes. `
    + 'Open Workshops to close its signups or move it to Past.';
}

/** What the cascade actually did, counted by the function that did it. */
export interface DeleteOfferingCounts {
  dancersTakenOff: number;
  weeklyTimesRemoved: number;
  sessionsCancelled: number;
}

/**
 * The line the Classes page shows afterwards. Every number here was measured
 * by the delete itself, never guessed from the page's own copy of anything.
 */
export function deleteOfferingSummary(name: string, counts: DeleteOfferingCounts): string {
  const parts = deleteOfferingCountPhrases(counts);
  if (parts.length === 0) {
    return `Deleted ${name}. It had nobody on it, no weekly times and nothing booked ahead.`;
  }
  return `Deleted ${name}. It ${joinList(parts)}. Sessions already in the past stay as history, `
    + 'and nobody was told.';
}

/**
 * "took 3 dancers off the roster", "removed 2 weekly times", "cancelled 18
 * upcoming bookings". Shared by the success line above and the blocked one
 * below so a Director reads the same words for the same facts whether the
 * delete finished or stopped. A count of zero is dropped rather than printed
 * as "0 weekly times": zero is a count too, and the sentence reads better
 * without it.
 */
function deleteOfferingCountPhrases(counts: DeleteOfferingCounts): string[] {
  const parts: string[] = [];
  if (counts.dancersTakenOff > 0) {
    parts.push(`took ${counts.dancersTakenOff} ${counts.dancersTakenOff === 1 ? 'dancer' : 'dancers'} off the roster`);
  }
  if (counts.weeklyTimesRemoved > 0) {
    parts.push(`removed ${counts.weeklyTimesRemoved} weekly ${counts.weeklyTimesRemoved === 1 ? 'time' : 'times'}`);
  }
  if (counts.sessionsCancelled > 0) {
    // "bookings", not "sessions": this count is every future reservation the
    // delete took off, and a class's rehearsals are in it (see the confirm
    // copy above). Calling a swept rehearsal a session would be a small
    // invented fact in a line whose whole job is to report measured ones.
    parts.push(`cancelled ${counts.sessionsCancelled} upcoming ${counts.sessionsCancelled === 1 ? 'booking' : 'bookings'}`);
  }
  return parts;
}

/** One thing the cascade could not take off, and the reason it gave. */
export interface DeleteOfferingBlocker {
  /** What it was, in a Director's words. */
  what: string;
  /** The raw reason, shown once so a refusal can be reported on. */
  why: string;
}

/**
 * The sentence a Director reads when the delete stopped partway.
 *
 * THE DANCERS ARE THE POINT OF THIS COPY. deleteOffering ends every
 * membership FIRST, before it touches a weekly time or a booking, so anything
 * refused afterwards leaves the class alive with its roster already emptied.
 * There is no rollback and no repair path for that: the "Class leftovers to
 * clean up" card in Studio Settings repairs bookings and orphaned weekly
 * times, never a membership. The old copy said "Everything else is already
 * clear", which is true and tells her nothing - a Director who read it, gave
 * up and kept the class was left with a live class whose roster had been
 * silently wiped, restorable only by re-enrolling each dancer by hand. So
 * what came off is counted in the same words the success summary uses, and
 * the dancer half says out loud what she now has to do about it.
 *
 * "Safe to try again" survives because it is still true: the cascade is
 * idempotent, and a retry finds the roster already empty, the removed weekly
 * times gone and the cancelled bookings gone. What a retry will NOT do is
 * report those first-run numbers a second time, which is the other reason
 * they have to be said here.
 */
export function deleteOfferingBlockedCopy(
  name: string,
  blocked: readonly DeleteOfferingBlocker[],
  done: DeleteOfferingCounts
): string {
  const whats = Array.from(new Set(blocked.map((b) => b.what)));
  const count = blocked.length === 1 ? 'something on it' : `${blocked.length} things on it`;
  const parts = deleteOfferingCountPhrases(done);
  const already = parts.length === 0
    ? 'Nothing came off the class. '
    : `Before it stopped it ${joinList(parts)}, and that stands. `;
  const dancers = done.dancersTakenOff > 0
    ? `${done.dancersTakenOff === 1 ? 'That dancer is' : 'Those dancers are'} already off the roster, `
      + 'so if you keep this class, check the roster. Put back on its row in Recently deleted classes, '
      + 'in Studio Settings, returns everything that came off. '
    : '';
  const why = blocked.length > 0 ? ` (${blocked[0].why})` : '';
  return `${name} was not deleted, because ${count} would not come off: ${whats.join('; ')}. `
    + `${already}${dancers}The class is still here and safe to try again. `
    + `Deleting it again keeps what already came off, so Undo after that returns all of it.${why}`;
}
